<template>
  <div
    class="mini-player"
    :class="{ 'mini-player--swiping': isSwiping }"
    :style="{
      '--player-progress': progressBarValue,
      '--player-swipe-offset': `${swipeOffset}px`,
      '--player-swipe-progress': swipeProgress,
    }"
  >
    <div
      class="mini-player__wrapper"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
    >
      <RouterLink :to="docLink" class="mini-player__info" @click="onClick()">
        <div class="mini-player__thumbnail">
          <img class="mini-player__cover" :src="doc.cover_image" />
        </div>
        <div class="mini-player__text">
          <h3 class="mini-player__title">{{ playerTitle }}</h3>
          <p v-if="playerSubtitle" class="mini-player__subtitle">
            {{ playerSubtitle }}
          </p>
        </div>
      </RouterLink>

      <div class="mini-player__controls">
        <button
          v-if="showPlaybackControls"
          class="mini-player__control mini-player__control--play"
          @click.stop="togglePlayback()"
        >
          <circle-spinner
            v-if="
              !playbackControl?.paused && playbackControl?.interruptedByNetwork
            "
            :animation-duration="2000"
            :size="58"
            :color="'#ffc906'"
          />
          <svg
            v-else-if="playbackControl?.paused"
            class="mini-player__control-icon mini-player__control-icon--play"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              d="M17.2 13.7a2 2 0 0 0 0-3.4L10 5.9a2 2 0 0 0-3 1.7v8.8a2 2 0 0 0 3 1.7z"
              fill="currentColor"
            />
          </svg>
          <svg
            v-else
            class="mini-player__control-icon mini-player__control-icon--pause"
            fill="none"
            viewBox="0 0 24 24"
          >
            <rect
              x="8"
              y="5"
              width="2"
              height="14"
              rx="1"
              fill="currentColor"
            />
            <rect
              x="14"
              y="5"
              width="2"
              height="14"
              rx="1"
              fill="currentColor"
            />
          </svg>
        </button>
        <RouterLink
          v-else
          :to="docLink"
          class="mini-player__control mini-player__control--chevron"
          @click="onClick()"
        >
          <span class="iconfont iarrow"></span>
        </RouterLink>
      </div>

      <button class="mini-player__close" @click.stop="dismiss()">
        <span class="iconfont iclose"></span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

import { Mixpanel } from '@/analytics/mixpanel'
import { assertNotNull } from '@/helpers/typing'
import { docRouteNames } from '@/models/doc'
import type { DocType } from '@/models/interfaces'
import { playerSession } from '@/services/player'

import CircleSpinner from '@/components/ui/CircleSpinner.vue'

const doc = computed(() => assertNotNull(playerSession.value.doc))
const route = useRoute()

// The controls are null for docs that have no audio, the player then renders
// as a link back to the doc, without the playback controls.
const playbackControl = computed(() => playerSession.value.playbackControl)
const progressControl = computed(() => playerSession.value.progressControl)

/**
 * The current chapter's title.
 */
const chapterTitle = computed(() => {
  const audio = playerSession.value.audio
  if (audio) {
    return audio.chapters.current().title
  }

  const currentChapter = doc.value.content.find(
    (content) => content.url_slug === doc.value.user.position_chapter,
  )
  return currentChapter?.title || doc.value.title
})

const playerTitle = computed(() => {
  return doc.value.doc_type === 'article' ? doc.value.title : chapterTitle.value
})

const playerSubtitle = computed(() => {
  return doc.value.doc_type === 'article' ? doc.value.author : doc.value.title
})

const progressBarValue = computed(() => {
  const value = progressControl.value
    ? progressControl.value.audioPositionPercent
    : doc.value.user.progress
  return `${value}%`
})

const docLink = computed(() => {
  return {
    name: docRouteNames[doc.value.doc_type as DocType].page,
    params: {
      url_slug: doc.value.url_slug,
      // Articles don't have chapters.
      ...(doc.value.doc_type !== 'article' && {
        page_url_slug: doc.value.user.position_chapter,
      }),
    },
  }
})

const showPlaybackControls = computed(() => {
  // Don't show the controls if the doc doesn't have any audio.
  const hasControls = playbackControl.value !== null
  // Don't show the playback controls if the user
  // hasn't started listening to the audio yet.
  const hasAudioProgress = doc.value.user.audio_position > 0

  return hasControls && hasAudioProgress
})

const swipeOffset = ref(0)
const swipeStartX = ref<number | null>(null)

const isSwiping = computed(() => swipeStartX.value !== null)

// The swipe distance, in pixels, that dismisses the player.
const dismissThreshold = 60

// How far the swipe is towards dismissing the player, from 0 to 1. The
// styles fade the player out as it goes.
const swipeProgress = computed(() =>
  Math.min(swipeOffset.value / dismissThreshold, 1),
)

onMounted(() => {
  Mixpanel.trackMiniPlayerShown(doc.value, playerState())
})

function togglePlayback(): void {
  const playback = assertNotNull(playbackControl.value)
  const wasPaused = playback.paused
  playerSession.value.togglePlayback({
    entryPoint: 'mini_player',
    referrerPageName: route.fullPath,
  })

  // The play and pause events report the state the tap leads to, which the
  // audio element only reaches a moment later.
  if (wasPaused) {
    Mixpanel.trackMiniPlayerPlay(doc.value, 'playing')
  } else {
    Mixpanel.trackMiniPlayerPause(doc.value, 'paused')
  }
}
function dismiss(): void {
  Mixpanel.trackMiniPlayerDismiss(doc.value, playerState())
  playerSession.value.stop('pause')
}

function onClick(): void {
  playerSession.value.clearNextArrival()
  Mixpanel.trackMiniPlayerClick(doc.value, playerState())
}

function onTouchStart(event: TouchEvent): void {
  swipeStartX.value = event.touches[0].clientX
}

function onTouchMove(event: TouchEvent): void {
  if (swipeStartX.value === null) {
    return
  }

  const distance = swipeStartX.value - event.touches[0].clientX
  // Only track leftwards swipes, the player does not move right.
  swipeOffset.value = Math.max(0, distance)
}

function onTouchEnd(): void {
  const dismissed = swipeOffset.value >= dismissThreshold

  swipeStartX.value = null
  swipeOffset.value = 0

  if (dismissed) {
    dismiss()
  }
}

function playerState(): 'playing' | 'paused' | 'resume' {
  if (!showPlaybackControls.value) {
    return 'resume'
  }

  return playerSession.value.isPlaying ? 'playing' : 'paused'
}
</script>

<style lang="scss" scoped>
.mini-player {
  position: fixed;
  inset: 0;
  z-index: 99;
  display: flex;
  align-items: flex-end;
  padding: 28px;
  pointer-events: none;

  @media (max-width: $w-tablet-s) {
    padding: 16px;
    padding-bottom: 70px;
  }

  &__wrapper {
    margin-inline: auto;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    width: 100%;
    max-width: 800px;
    border-radius: 8px;
    border: 1px solid $c-border;
    border-bottom: none;
    background: $c-white;
    filter: $shadow-f;
    pointer-events: auto;
    transition: all 0.3s;
    transform: translateX(calc(var(--player-swipe-offset) * -1));
    opacity: calc(1 - var(--player-swipe-progress) * 0.4);

    &:has(.mini-player__info:hover) {
      background: $c-bg;
    }

    &:before,
    &:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      z-index: 1;
      height: 4px;
      border-radius: 0 0 8px 8px;
    }

    &:before {
      width: 100%;
      background: $c-bg;
    }

    &:after {
      width: var(--player-progress);
      background: $c-yellow;
    }
  }

  &--swiping &__wrapper {
    transition: none;
  }

  &__info {
    display: flex;
    text-align: left;
    height: 100%;
    min-width: 0;
  }

  &__text {
    padding: 12px 16px;
    flex: 1;
    overflow: hidden;
  }

  &__title {
    display: block;
    font-size: 12px;
    line-height: 16px;
    color: $c-black-15;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  &__subtitle {
    display: block;
    font-size: 12px;
    line-height: 16px;
    color: $c-black-3;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  &__thumbnail {
    display: flex;
    justify-content: center;
    align-items: center;
    background: $c-bg;
    width: 56px;
    height: 56px;
    padding: 6px;
    border-radius: 8px 0 0 8px;
  }

  &__cover {
    max-height: 100%;
    filter: $shadow-f;
    transition: all 0.3s;
  }

  &:has(.mini-player__info:hover) &__cover {
    transform: scale(1.05);
  }

  &__controls {
    margin-right: 12px;
    color: $c-black-15;
    flex-shrink: 0;
  }

  &__control {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 36px;
    height: 36px;
    border-radius: 36px;
    color: $c-black-15;
    --iconSize: 24px;

    @include hover {
      background: $c-border;
    }

    .iconfont,
    &-icon {
      width: var(--iconSize);
      height: var(--iconSize);
      line-height: var(--iconSize);
      font-size: var(--iconSize);
    }
  }

  &__control--chevron {
    --iconSize: 20px;
  }

  &__close {
    position: absolute;
    right: -13px;
    top: -13px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 28px;
    border: 1px solid $c-border;
    background: $c-white;
    filter: $shadow-f;
    opacity: 0;
    transition: all 0.3s;

    .iconfont {
      width: 11px;
      height: 11px;
      line-height: 11px;
      font-size: 11px;
      margin-top: -1px;
      color: $c-black-3;
    }

    &:hover {
      background: $c-bg;
      border-color: $c-border-2;
    }
  }

  &:has(.mini-player__info:hover) &__close,
  &__close:hover {
    opacity: 1;
  }
}

.night-mode .mini-player {
  &__wrapper {
    border-color: $c-black-9;
    background: $c-black-8;

    &:has(.mini-player__info:hover) {
      background: $c-black-9;
    }

    &:before {
      background: $c-black-9;
    }
  }

  &__title {
    color: $c-white;
  }

  &__subtitle {
    color: $c-dark-6;
  }

  &__thumbnail {
    background: $c-black-9;
  }

  &__control {
    color: $c-dark-6;

    @include hover {
      background: $c-black-9;
    }
  }

  &__controls {
    color: $c-white;
  }

  &__close {
    border-color: $c-black-9;
    background: $c-black-8;

    .iconfont {
      color: $c-dark-6;
    }

    &:hover {
      border-color: $c-black-10;
      background: $c-black-9;
    }
  }
}
</style>
